<template>
  <div
    class="settings-sidebar"
    :class="{
      'sidebar-collapsed': isCollapsed,
      'sidebar-hovered': isHovered,
      'mobile-open': !isCollapsed && isMobile,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Sidebar Header -->
    <div class="sidebar-header">
      <div class="header-content">
        <div class="settings-icon-wrapper">
          <v-icon class="settings-icon" color="white" size="28">mdi-cog</v-icon>
        </div>
        <h2 class="settings-title">{{ $t("settings") }}</h2>
        <v-btn
          icon
          small
          class="collapse-btn"
          @click="toggleSidebar"
          color="white"
        >
          <v-icon size="20">
            {{ isCollapsed ? "mdi-chevron-right" : "mdi-chevron-left" }}
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Categories List -->
    <div class="categories-container">
      <div class="categories-list">
        <div
          v-for="(category, index) in categories"
          :key="category.title"
          class="category-item"
          :class="{
            'category-active': isActiveCategory(category.title),
            'category-admin': category.title === 'Site settings',
          }"
          @click="selectCategory(category)"
          :style="{ animationDelay: `${index * 50}ms` }"
        >
          <!-- Category Icon -->
          <div class="category-icon">
            <img
              :src="
                isActiveCategory(category.title)
                  ? category.image_selected
                  : category.image
              "
              :alt="category.title"
              class="category-image"
            />
          </div>

          <!-- Category Content -->
          <div class="category-content">
            <span class="category-title">{{ category.title }}</span>
            <span class="category-description">{{
              getCategoryDescription(category.title)
            }}</span>
          </div>

          <!-- Active Indicator -->
          <div
            class="active-indicator"
            v-if="isActiveCategory(category.title)"
          ></div>

          <!-- Admin Badge -->
          <v-chip
            v-if="category.title === 'Site settings'"
            x-small
            color="orange"
            text-color="white"
            class="admin-badge"
          >
            <v-icon left x-small>mdi-shield-crown</v-icon>
            ADMIN
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Sidebar Footer -->
    <div class="sidebar-footer">
      <v-divider class="footer-divider"></v-divider>
      <div class="footer-content">
        <div class="user-info">
          <v-avatar size="32" class="user-avatar">
            <img
              v-if="profile.image"
              :src="profile.midThumbnail"
              alt="avatar"
            />
            <v-icon v-else color="white">mdi-account</v-icon>
          </v-avatar>
          <div class="user-details">
            <span class="user-name" v-if="profileType === 'Volunteer'">
              {{ profile.first_name + " " + profile.last_name || "User" }}
            </span>
            <span class="user-name" v-if="profileType === 'Agency'">
              {{ profile.name || "User" }}
            </span>
            <span class="user-status">{{ profileType }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Overlay -->
    <div
      class="mobile-overlay"
      v-if="showMobileOverlay"
      @click="closeMobileSidebar"
    ></div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "EnhancedSettingsSidebar",
  data() {
    return {
      isCollapsed: false,
      isHovered: false,
      showMobileOverlay: false,
      isMobile: false,
      categories: [
        {
          title: "Account",
          image: require("@/assets/svg/settings/icon_account_setting.svg"),
          image_selected: require("@/assets/svg/settings/icon_account_setting_selected.svg"),
        },
        {
          title: "Notifications",
          image: require("@/assets/svg/settings/icon_notification.svg"),
          image_selected: require("@/assets/svg/settings/icon_notification_selected.svg"),
        },
        {
          title: "Password",
          image: require("@/assets/svg/settings/icon_password_setting.svg"),
          image_selected: require("@/assets/svg/settings/icon_password_setting_selected.svg"),
        },
        {
          title: "Language and region",
          image: require("@/assets/svg/settings/icon_language_setting.svg"),
          image_selected: require("@/assets/svg/settings/icon_language_setting_selected.svg"),
        },
        {
          title: "Blocked",
          image: require("@/assets/svg/settings/icon_blocked.svg"),
          image_selected: require("@/assets/svg/settings/icon_blocked_selected.svg"),
        },
        {
          title: "Back",
          image: require("@/assets/svg/settings/icon_back.svg"),
          image_selected: require("@/assets/svg/settings/icon_back.svg"),
        },
      ],
    };
  },
  computed: {
    ...mapState("auth", {
      profileType: "type",
      profile: "profile",
    }),
    currentCategory() {
      return this.$route.query.category || "Account";
    },
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      if (this.isMobile && !this.isCollapsed) {
        this.showMobileOverlay = true;
      } else {
        this.showMobileOverlay = false;
      }
    },
    selectCategory(category) {
      if (category.title === "Back") {
        // Navigate to previous page
        if (window.history.length > 1) {
          this.$router.back();
        } else {
          // fallback: go to dashboard
          this.$router.push({ name: "Dashboard" });
        }
        return;
      }

      this.$router.push({ query: { category: category.title } });
      if (this.isMobile) {
        this.isCollapsed = true;
        this.showMobileOverlay = false;
      }
    },

    isActiveCategory(title) {
      return this.currentCategory === title;
    },
    getCategoryDescription(title) {
      const descriptions = {
        Account: "Profile & personal info",
        Notifications: "Alerts & preferences",
        Password: "Security settings",
        "Language and region": "Locale preferences",
        Blocked: "Privacy controls",
        "Site settings": "Admin configuration",
        Back: "Dashboard",
      };
      return descriptions[title] || "";
    },
    onMouseEnter() {
      if (this.isCollapsed && !this.isMobile) {
        this.isHovered = true;
      }
    },
    onMouseLeave() {
      if (this.isCollapsed) {
        this.isHovered = false;
      }
    },
    closeMobileSidebar() {
      this.showMobileOverlay = false;
      this.isCollapsed = true;
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.isCollapsed = true;
      }
    },
  },
  mounted() {
    if (!this.$route.query.category) {
      this.$router.replace({ query: { category: "Account" } });
    }
    if (this.profileType === "Admin") {
      this.categories.push({
        title: "Site settings",
        image: require("@/assets/svg/settings/icon_site_settings.svg"),
        image_selected: require("@/assets/svg/settings/icon_site_settings_selected.svg"),
      });
    }

    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
};
</script>

<style scoped>
.settings-sidebar {
  position: sticky;
  top: 56px;
  height: calc(100vh - 56px);
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  background: linear-gradient(180deg, #2c3e50 0%, #34495e 100%);
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 0 16px 16px 0;
  overflow: hidden;
  z-index: 190;
  will-change: transform, width;
}

/* Collapsed */
.sidebar-collapsed {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
}

/* Hover expand */
.sidebar-collapsed.sidebar-hovered {
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.2);
}

/* Header */
.sidebar-header {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}
.sidebar-collapsed .sidebar-header {
  padding: 24px 16px;
}
.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
}
.settings-icon-wrapper {
  transition: transform 0.3s ease;
}
.sidebar-collapsed .settings-icon-wrapper {
  transform: scale(1.1);
}
.settings-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
  transition: all 0.2s ease;
  opacity: 1;
  transform: translateX(0);
}
.sidebar-collapsed .settings-title {
  opacity: 0;
  transform: translateX(-10px);
}
.sidebar-collapsed.sidebar-hovered .settings-title {
  opacity: 1;
  transform: translateX(0);
  transition-delay: 0.1s;
}

.collapse-btn {
  transition: all 0.3s ease !important;
}
.sidebar-collapsed .collapse-btn {
  transform: rotate(180deg);
}
.sidebar-collapsed.sidebar-hovered .collapse-btn {
  transform: rotate(0deg);
}

/* Categories */
.categories-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}
.categories-container::-webkit-scrollbar {
  width: 4px;
}
.categories-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.categories-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
}
.category-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  animation: slideIn 0.4s ease-out forwards;
  opacity: 0;
  transform: translateX(-10px);
}
@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.category-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}
.category-item.category-active {
  background: rgba(52, 152, 219, 0.15);
  border-color: rgba(52, 152, 219, 0.3);
}
.category-item.category-active:hover {
  background: rgba(52, 152, 219, 0.2);
}
.category-icon {
  min-width: 24px;
  width: 24px;
  height: 24px;
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: margin 0.3s ease;
}
.sidebar-collapsed .category-icon {
  margin-right: 0;
}
.sidebar-collapsed.sidebar-hovered .category-icon {
  margin-right: 16px;
}
.category-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}
.category-item:hover .category-image {
  transform: scale(1.1);
}
.category-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  width: auto;
  height: auto;
  overflow: visible;
}
.sidebar-collapsed .category-content {
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  margin: 0;
}
.sidebar-collapsed.sidebar-hovered .category-content {
  opacity: 1;
  width: auto;
  height: auto;
  overflow: visible;
  margin: initial;
}
.category-title {
  color: white;
  font-size: 1rem;
  font-weight: 500;
  transition: color 0.2s ease;
}
.category-item:hover .category-title {
  color: #3498db;
}
.category-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  transition: all 0.2s ease;
}
.category-item:hover .category-description {
  color: rgba(255, 255, 255, 0.9);
}
.active-indicator {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: #4caf50;
  border-radius: 2px;
  animation: pulse 2s infinite;
  transition: opacity 0.3s ease;
}
.sidebar-collapsed .active-indicator {
  opacity: 0;
  right: 8px;
}
.sidebar-collapsed.sidebar-hovered .active-indicator {
  opacity: 1;
  right: 16px;
}
.admin-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.7rem;
  height: 20px;
  transition: all 0.3s ease;
  opacity: 1;
}
.sidebar-collapsed .admin-badge {
  opacity: 0;
  transform: scale(0.8);
}
.sidebar-collapsed.sidebar-hovered .admin-badge {
  opacity: 1;
  transform: scale(1);
}

/* Footer */
.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.1);
  transition: padding 0.3s ease;
}
.sidebar-collapsed .sidebar-footer {
  padding: 20px 12px;
}
.footer-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
  margin-bottom: 16px;
}
.footer-content {
  display: flex;
  align-items: center;
  gap: 12px;
}
.user-avatar {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: all 0.3s ease;
  opacity: 1;
  overflow: hidden;
  width: auto;
}
.sidebar-collapsed .user-details {
  opacity: 0;
  width: 0;
  height: 0;
}
.sidebar-collapsed.sidebar-hovered .user-details {
  opacity: 1;
  width: auto;
  height: auto;
}
.user-name {
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-status {
  color: #4caf50;
  font-size: 0.75rem;
  white-space: nowrap;
}

/* Mobile overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: none;
  animation: fadeIn 0.3s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .settings-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 320px;
    min-width: 320px;
    max-width: 320px;
    border-radius: 0;
    transform: translateX(-100%);
    z-index: 1001;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .settings-sidebar.mobile-open {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
  }

  .mobile-overlay {
    display: block;
  }

  .sidebar-collapsed {
    transform: translateX(-100%);
  }

  .sidebar-collapsed.sidebar-hovered {
    width: 320px;
    min-width: 320px;
    max-width: 320px;
  }
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>